import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {EfeResponse, Field} from 'commons/models';
import {EfeAccordion} from 'components';
import {EfeAccordionValues} from 'commons';
import {MainContent} from 'layouts/commons';
import {ConsigneeData} from 'commons/types/Types';
import PersonalInfoForm from './form/PersonalInfoForm';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {ClientService} from 'services';
import {nextStep} from 'commons/utils/Functions';

export default function ConsigneePersonalInfo() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {expedientId, setKycId, menu, fatherKycId, kycId} = useMenuContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [consigneeData, setConsigneeData] = React.useState<ConsigneeData>();
    const {enqueueSnackbar} = useSnackbar();

    async function onSubmit(values: ConsigneeData) {
        setIsLoading(true);
        try {
            const response = await ClientService.saveConsigneeData(values);
            if (response && response.data && response.data.kycId) {
                setKycId(response.data.kycId);
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu, true);
                setIsLoading(false);
                if (Constants.MODULE_STEP !== path) {
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                }
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        }
    }

    React.useEffect(() => {
        if (kycId > 0) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<ConsigneeData> =
                        await ClientService.getConsigneeData(kycId);
                    if (response && response.data) {
                        setConsigneeData(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                }
            })();
        }else{
            setConsigneeData(undefined);
        }
    }, [kycId]);

    const accordion: EfeAccordionValues = {
        icon: <ActionIcon />,
        title: 'Informaci\u00F3n personal',
        reactElement: (
            <Box mt={8}>
                {!isLoading && (
                    <PersonalInfoForm
                        key={`${Math.random}`}
                        auxConsignee={consigneeData}
                        submitHandler={onSubmit}
                        fields={viewFields}
                        fatherKycId={fatherKycId}
                        expedientId={expedientId}
                        flow={flow}
                    />
                )}
            </Box>
        ),
        expandIcon: true,
        disabled: false,
        expanded: ViewKeys.V_CONSIGNEE_INFO === flow.data.expandKey,
    };

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.CONSIGNEE_INFO)}
                values={[accordion]}
            />
        </MainContent>
    );
}
